export type PrincipleId = 'charity' | 'unity' | 'fraternity' | 'patriotism';

export type Principle = {
  id: PrincipleId;
  title: string;
  tagline: string;
  quote: string;
  quoteSource: string;
  body: string[];
  highlights: { title: string; text: string }[];
  learnMoreHref: string;
  learnMoreLabel: string;
};

export const PRINCIPLES: Principle[] = [
  {
    id: 'charity',
    title: 'Charity',
    tagline: 'We find Christ in our neighbor -- and we act.',
    quote: 'We find Christ in our neighbor and are changed by each encounter.',
    quoteSource: 'Knights of Columbus, Our Mission (kofc.org)',
    body: [
      'Charity is the first principle of the Knights of Columbus. From the beginning, Blessed Michael McGivney founded the Order so Catholic men would protect families in hardship and serve neighbors in need. That same call shapes Council 1056 at St. Mary Parish in Delaware, Ohio.',
      'Across the Order, Knights put Charity into action through Faith in Action programs such as Food for Families, which helps councils support local food pantries, banks, and soup kitchens, and Coats for Kids, which has distributed more than a million winter coats to children in need across North America.',
      'Closer to home, our charitable work includes parish support, community fundraisers, quiet help for families who are struggling, and programs such as the Lucky Number Calendar that raise funds for local needs we can see and serve.',
    ],
    highlights: [
      {
        title: 'Food for Families',
        text: 'A featured K of C program that helps councils donate food and funds so fewer neighbors go to bed hungry.',
      },
      {
        title: 'Coats for Kids',
        text: 'Councils distribute new winter coats to children in need; the Order reports more than a million coats given since the program began.',
      },
      {
        title: 'Council 1056',
        text: 'We keep charity close to home: parish events, community drives, and support for families connected to St. Mary and Delaware.',
      },
    ],
    learnMoreHref: 'https://www.kofc.org/who-we-are/our-story/',
    learnMoreLabel: 'Our Mission on kofc.org',
  },
  {
    id: 'unity',
    title: 'Unity',
    tagline: 'Stronger together in one Catholic brotherhood.',
    quote:
      'United in faith as members of the universal Church, our diverse brotherhood builds up the Body of Christ.',
    quoteSource: 'Knights of Columbus, Our Mission (kofc.org)',
    body: [
      'Unity was one of the Order\'s founding principles alongside Charity. Father McGivney gathered Catholic men so they would stand together -- in faith, in mutual aid, and in service -- when prejudice and hardship pressed on Catholic families in the late 1800s.',
      'Today Unity still means that a diverse brotherhood, united in the Church, accomplishes far more than any one man alone. We do not always agree on every detail, but a brother Knight can count on encouragement and help as we serve our parish and community.',
      'In Council 1056, Unity looks like showing up for meetings, projects, and one another so the work of the council never rests on one man\'s shoulders.',
    ],
    highlights: [
      {
        title: 'Founded in unity',
        text: 'Charity and Unity were the first principles of the Knights when the Order was incorporated in 1882.',
      },
      {
        title: 'One Church',
        text: 'Our brotherhood spans cultures and backgrounds while remaining rooted in the Catholic faith.',
      },
      {
        title: 'Local council life',
        text: 'Shared projects, meetings, and parish service keep Unity practical at St. Mary Parish.',
      },
    ],
    learnMoreHref: 'https://www.kofc.org/who-we-are/our-history/',
    learnMoreLabel: 'Order history on kofc.org',
  },
  {
    id: 'fraternity',
    title: 'Fraternity',
    tagline: 'Christ-centered brotherhood that watches out for one another.',
    quote:
      'We seek Christ-centered brotherhood, encouraging one another to grow in holiness and virtue.',
    quoteSource: 'Knights of Columbus, Our Mission (kofc.org)',
    body: [
      'Fraternity grew from Father McGivney\'s desire that Catholic families would not face hardship alone. Friendship, prayer, mutual support, and practical help when a brother or his family is in need remain at the heart of council life.',
      'The Order continues that mission through initiatives such as Cor, which gathers men for prayer, faith formation, and fraternity so they grow as husbands, fathers, and disciples of Jesus. Cor is open to parish men and centers brotherhood on Christ, not just social time.',
      'At Council 1056 we try to live Fraternity simply: check in on one another, pray together, celebrate milestones, and show up when a family needs a hand.',
    ],
    highlights: [
      {
        title: 'Protecting families',
        text: 'From the start, Knights offered mutual aid so widows and children would not be left without support.',
      },
      {
        title: 'Cor',
        text: 'A K of C initiative that forms men through prayer, formation, and Christ-centered fraternity.',
      },
      {
        title: 'Brotherhood at 1056',
        text: 'We grow in holiness and virtue side by side -- better husbands, fathers, sons, and neighbors.',
      },
    ],
    learnMoreHref: 'https://www.kofc.org/resources/faith-formation/mens-faith-formation/cor/why-cor/',
    learnMoreLabel: 'Why Cor on kofc.org',
  },
  {
    id: 'patriotism',
    title: 'Patriotism',
    tagline: 'Faithful Catholics. Faithful citizens.',
    quote:
      'We strive to be faithful citizens, bearing witness to the true freedom that comes from pursuing lives of truth, justice, solidarity and above all, charity.',
    quoteSource: 'Knights of Columbus, Our Mission (kofc.org)',
    body: [
      'When the Knights were founded in 1882, three principles united members: Charity, Unity, and Fraternity. Patriotism was added in 1900 because Catholics were often unfairly suspected of divided loyalty. The Fourth Degree teaches that Knights are faithful to both God and country.',
      'Fourth Degree members -- Sir Knights -- live out Patriotism through color and honor guards, programs that promote Catholic citizenship, and outreach to veterans and active military. A major initiative, Serving Those Who Served, supports Veterans Affairs facilities across the United States.',
      'Council 1056 honors those who serve in uniform, respects the freedoms we share, and tries to live citizenship marked by truth, justice, solidarity, and charity -- as the Order\'s own mission statement teaches.',
    ],
    highlights: [
      {
        title: 'Added in 1900',
        text: 'Patriotism became the Order\'s fourth principle; the first Fourth Degree exemplification was held Feb. 22, 1900, in New York City.',
      },
      {
        title: 'Fourth Degree',
        text: 'Assemblies organize honor guards and citizenship programs; there are thousands of assemblies worldwide.',
      },
      {
        title: 'Serving Those Who Served',
        text: 'Fourth Degree Knights support VA facilities and veterans\' needs across the country.',
      },
    ],
    learnMoreHref:
      'https://www.kofc.org/resources/news/latest-news/who-are-the-4th-degree-knights/',
    learnMoreLabel: 'Fourth Degree on kofc.org',
  },
];

export function getPrinciple(id: string | undefined): Principle | undefined {
  return PRINCIPLES.find((p) => p.id === id);
}